import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/features/auth/hooks/use-auth';
import { usePermisosDeReglas } from '@/features/pricing-rules/lib/use-permisos-de-reglas';
import { useCambiosNuevos } from '@/features/smart-pricing/hooks/use-cambios-nuevos';
import type {
  DestinoHoy,
  FiltroBandeja,
} from '@/features/smart-pricing/types/price-proposals.types';
import { RUTAS_COMPITE } from '../lib/rutas';

export {
  RUTAS_COMPITE,
  VISTAS_DE_ESTRATEGIA,
  esVistaDeEstrategia,
  type VistaDeEstrategia,
} from '../lib/rutas';

/**
 * Lo que viaja de Decisiones a Revisar precios además de la URL: el filtro con
 * que se llega (cambios grandes, conflictos entre reglas, las que cuidó una
 * regla). Es estado de navegación, igual que en la página vieja: llega una vez y
 * no se queda pegado.
 */
export interface EstadoDeLlegada {
  filtro?: FiltroBandeja | null;
}

/**
 * Quién ve qué. Las mismas dos condiciones que la página vieja, sin cambiarlas:
 *
 * - Reglas y Productos se ven con el permiso de ver reglas (26-sep: cinco
 *   permisos propios; cualquiera de escritura implica ver).
 * - Decisiones y Estrategias, con el módulo `data-import`. Un módulo «Bloqueado»
 *   para el rol está en `menuItems` y `canSeeMenu` da true: lo corta `isModuleLocked`.
 *   Sin `menuItems` (sesión vieja) no se bloquea, igual que `ProtectedRoute`.
 */
export function usePermisosDeCompite() {
  const { ver: veReglas } = usePermisosDeReglas();
  const { canSeeMenu, menuItems, isModuleLocked } = useAuth();
  const veEstrategias =
    !isModuleLocked('data-import') && (menuItems.length === 0 || canSeeMenu('data-import'));
  return { veReglas, veEstrategias };
}

/**
 * La coordinación que antes hacía `SmartPricingPage` entre sus pestañas, ahora
 * entre pantallas:
 *
 * - la hoja de una estrategia vencida («la vuelta», `?vuelta=<id>`), que se abre
 *   encima de cualquier pantalla;
 * - «Hay cambios nuevos · Recargar» y la llave que vuelve a pedir los datos;
 * - `irA`: una decisión de Decisiones lleva al lugar donde se decide con el
 *   contexto completo (Revisar precios ya filtrado; una corrida fallida, a
 *   Estrategias).
 */
export function useOrquestacionCompite() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const vuelta = params.get('vuelta');
  const [refreshKey, setRefreshKey] = useState(0);
  const cambios = useCambiosNuevos();

  // `prev` y no `params`: dos cambios en el mismo clic no se pisan (plan 2 T5).
  const abrirVuelta = (id: string | null) =>
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (id) p.set('vuelta', id);
        else p.delete('vuelta');
        return p;
      },
      { replace: true }
    );

  const recargar = () => {
    setRefreshKey((k) => k + 1);
    cambios.aceptar();
  };

  const irA = (
    destino: DestinoHoy,
    strategyApplicationId: string | null,
    filtro?: FiltroBandeja
  ) => {
    // La vuelta abre su hoja encima de donde se está (plan 2).
    if (destino === 'VUELTA') {
      abrirVuelta(strategyApplicationId);
      return;
    }
    if (destino === 'BANDEJA') {
      const q = new URLSearchParams();
      if (strategyApplicationId) q.set('estrategia', strategyApplicationId);
      if (filtro?.productId) q.set('producto', filtro.productId);
      const busqueda = q.toString();
      const estado: EstadoDeLlegada = { filtro: filtro ?? null };
      navigate(`${RUTAS_COMPITE.revisar}${busqueda ? `?${busqueda}` : ''}`, { state: estado });
      return;
    }
    navigate(`${RUTAS_COMPITE.estrategia}?tab=estrategias`);
  };

  return {
    vuelta,
    abrirVuelta,
    refreshKey,
    /** Cuando algo se resolvió afuera (la hoja de la vuelta): vuelve a pedir los datos. */
    alResolver: () => setRefreshKey((k) => k + 1),
    hayCambiosNuevos: cambios.hay,
    recargar,
    irA,
  };
}
